/**
 * Quote Approval Defaults — iter150AI.
 *
 * Tiny admin page that lets a super-admin manage the list of default
 * recipients who receive the dealer-quote approval emails (in addition
 * to the customer's Answered By rep + the dealer's own email).
 */
import { useEffect, useState, useCallback } from "react";
import { EnvelopeSimple, FloppyDisk, ArrowsClockwise, Trash, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import AdminPageHeader from "@/components/AdminPageHeader";
import { fetchApprovalEmails, saveApprovalEmails } from "@/lib/api";

export default function QuoteApprovalEmailsPage({ user, onLogout, onLogoutAll }) {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchApprovalEmails();
      setEmails(r.emails || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load approval emails");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  function add() { setEmails(prev => [...prev, ""]); }
  function patch(idx, val) {
    setEmails(prev => prev.map((e, i) => i === idx ? val : e));
  }
  function remove(idx) {
    setEmails(prev => prev.filter((_, i) => i !== idx));
  }

  async function save() {
    if (saving) return;
    const cleaned = emails.map(e => (e || "").trim()).filter(e => e.includes("@"));
    setSaving(true);
    try {
      const r = await saveApprovalEmails(cleaned);
      setEmails(r.emails || []);
      toast.success(`Saved ${r.emails.length} email${r.emails.length === 1 ? "" : "s"}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Save failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="quote-approval-emails-page">
      <AdminPageHeader title="Quote Approval Emails" user={user}
                       onLogout={onLogout} onLogoutAll={onLogoutAll} />
      <main className="max-w-3xl mx-auto px-4 md:px-8 py-6 space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-[12px] text-blue-900 leading-snug">
          <b>How this works:</b> When a dealer submits a quote for approval,
          the system emails it to: <i>the customer's "Answered By" rep</i> +
          <i>the dealer's own email</i> + <b>everyone on the list below</b>.
          Each recipient can approve or reject the quote with one click from
          the email. The approver becomes the recorded "Quote Prepared By"
          on approval and the quote is locked from further dealer edits.
        </div>

        <div className="bg-white border border-slate-200 rounded-sm">
          <div className="px-3 py-2 border-b border-slate-200 flex items-center gap-2">
            <EnvelopeSimple size={14} weight="bold" className="text-slate-500" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex-1">
              Default approver list
            </h2>
            <button onClick={refresh} disabled={loading}
                    data-testid="qa-emails-refresh"
                    className="text-[10px] uppercase font-bold px-2 py-1 rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center gap-1 disabled:opacity-50">
              <ArrowsClockwise size={10} weight="bold" />
              {loading ? "…" : "Refresh"}
            </button>
            <button onClick={save} disabled={saving || loading}
                    data-testid="qa-emails-save"
                    className="text-[10px] uppercase font-bold px-3 py-1 rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white inline-flex items-center gap-1 disabled:opacity-50">
              <FloppyDisk size={10} weight="bold" />
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
          <ul className="divide-y divide-slate-100">
            {emails.length === 0 && !loading && (
              <li className="px-3 py-6 text-center text-[12px] text-slate-500"
                  data-testid="qa-emails-empty">
                No default approval emails yet. The customer's Answered By rep
                + the dealer email are still included; you can add more here.
              </li>
            )}
            {emails.map((em, idx) => (
              <li key={idx} className="px-3 py-2 flex items-center gap-2"
                  data-testid={`qa-emails-row-${idx}`}>
                <input type="email" value={em}
                       onChange={e => patch(idx, e.target.value)}
                       placeholder="approver@example.com"
                       data-testid={`qa-emails-input-${idx}`}
                       className="flex-1 text-sm px-2 py-1.5 border border-slate-300 rounded-sm font-mono" />
                <button onClick={() => remove(idx)}
                        data-testid={`qa-emails-remove-${idx}`}
                        className="w-7 h-7 inline-flex items-center justify-center rounded-sm text-slate-400 hover:text-red-700 hover:bg-red-50">
                  <Trash size={12} weight="bold" />
                </button>
              </li>
            ))}
          </ul>
          <div className="px-3 py-2 border-t border-slate-200">
            <button onClick={add}
                    data-testid="qa-emails-add"
                    className="text-[10px] uppercase font-bold px-3 py-1.5 rounded-sm bg-blue-700 hover:bg-blue-800 text-white inline-flex items-center gap-1.5">
              <Plus size={10} weight="bold" />
              Add email
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
